<template>
  <article
    v-if="tile"
    class="cms-colorlist"
    :class="{
      'cms-colorlist--closed': tile.state === constants.MARKET_CLOSED,
      'cms-colorlist--settled': tile.state === constants.MARKET_SETTLED,
    }"
    :data-colorlist-region="settings.regionUUID"
    @click="navigate"
  >
    <header class="cms-colorlist__header">
      <div
        v-if="
          tile.shortName || tile.raceStatus || tile.date || tile.state !== constants.MARKET_OPEN
        "
        class="cms-colorlist__meta"
      >
        <span class="cms-colorlist__category">
          <span v-if="tile.shortName">{{ tile.shortName }}</span>
          <span
            v-if="tile.shortName && tile.raceStatus"
            class="cms-colorlist__separator"
            aria-hidden="true"
          >
            ·
          </span>
          <span
            v-if="tile.raceStatus"
            class="cms-colorlist__status"
            :style="{
              '--status-light': tile.raceStatus.textColors[0],
              '--status-dark': tile.raceStatus.textColors[1],
            }"
            >{{ tile.raceStatus.label }}</span
          >
        </span>
        <span
          v-if="tile.date || tile.state !== constants.MARKET_OPEN"
          class="cms-colorlist__date"
          >{{
            tile.state === constants.MARKET_CLOSED
              ? 'Awaiting result'
              : tile.state === constants.MARKET_SETTLED
                ? 'Closed'
                : tile.date
          }}</span
        >
      </div>
      <h3 class="cms-colorlist__title">
        <router-link :to="route()" :target="linkTarget" rel="noopener" @click="track">{{
          tile.title
        }}</router-link>
      </h3>
      <p v-if="summaryText" class="cms-colorlist__summary">{{ summaryText }}</p>
    </header>
    <div class="cms-colorlist__rows">
      <div
        v-for="row in tile.rows"
        :key="`${row.id}-${row.direction}`"
        class="cms-colorlist__row"
        :class="{
          'cms-colorlist__row--leader':
            tile.state === constants.MARKET_SETTLED ? row.isWinner : row.isLeader,
          'cms-colorlist__row--defeated': row.isDefeated,
        }"
        :data-contract-id="row.id"
        :data-direction="row.direction"
        :style="{ '--contract-color': row.color }"
      >
        <span class="cms-colorlist__bar" aria-hidden="true"></span>
        <span
          class="cms-colorlist__portrait"
          :class="{ 'cms-colorlist__portrait--no': row.dimImage }"
          aria-hidden="true"
        >
          <span v-if="row.overlay">{{ row.overlay }}</span>
          <img
            v-else-if="row.image && !failedImages[row.id]"
            :src="row.image"
            alt=""
            @error="failedImages[row.id] = true"
          />
          <span v-else>{{ initials(row.name) }}</span>
        </span>
        <div class="cms-colorlist__identity">
          <div class="cms-colorlist__name-line">
            <span
              v-if="row.isLeader && tile.state !== constants.MARKET_SETTLED"
              class="visually-hidden"
              >Leading contract</span
            >
            <router-link
              class="cms-colorlist__name"
              :to="route(row.id, row.direction)"
              :target="linkTarget"
              rel="noopener"
              @click="track"
              >{{ row.name }}</router-link
            >
            <span
              v-if="row.isWinner"
              class="cms-colorlist__winner-icon"
              role="img"
              aria-label="Winner"
            ></span>
          </div>

          <span v-if="row.subtitle" class="cms-colorlist__subtitle">{{ row.subtitle }}</span>
        </div>
        <svg
          v-if="row.sparkline"
          class="cms-colorlist__sparkline"
          :class="{ 'is-up': row.sparkline.trend > 0, 'is-down': row.sparkline.trend < 0 }"
          viewBox="0 0 64 30"
          role="img"
          :aria-label="`${row.name}: ${tile.numDays}-day price history`"
        >
          <line class="cms-colorlist__reference" x1="2" x2="62" y1="15" y2="15" />
          <path
            :d="row.sparkline.path"
            fill="none"
            stroke="currentColor"
            stroke-width="1.2"
            stroke-linejoin="round"
          />
        </svg>
        <div class="cms-colorlist__quote">
          <span
            v-if="tile.state === constants.MARKET_SETTLED"
            class="cms-colorlist__price cms-colorlist__price--final"
            :aria-label="`Final price for ${row.name}, ${row.display}`"
            @click.stop
            >{{ row.display }}</span
          >
          <button
            v-else
            type="button"
            class="cms-colorlist__price"
            :data-price="row.display"
            :disabled="disabled(row)"
            :aria-label="`Trade ${row.name}, ${row.display}`"
            @click.stop="trade(row, $event)"
          >
            {{ row.display }}
          </button>
          <span v-if="tile.state === constants.MARKET_SETTLED" class="cms-colorlist__final"
            >Final</span
          >
          <span
            v-else-if="row.price !== null"
            class="cms-colorlist__change"
            :class="{ 'is-up': row.change > 0, 'is-down': row.change < 0 }"
          >
            <market-change-price
              v-if="row.change !== null && row.change !== 0"
              :price="row.change"
              :symbol="tile.symbol"
            />
            <span v-else>–</span
            ><span v-if="tile.changeLabel" class="cms-colorlist__days">{{ tile.changeLabel }}</span>
          </span>
        </div>
      </div>
    </div>
    <footer class="cms-colorlist__footer">
      <span v-if="tile.state === constants.MARKET_SETTLED" class="cms-colorlist__settlement">{{
        tile.settlementLabel
      }}</span>
      <span v-else-if="tile.showSpread" class="cms-colorlist__spread"
        >{{ tile.spreadLabel }}
        {{ tile.spread === null ? '—' : `${tile.spread}${tile.spreadSymbol}` }}</span
      >
      <router-link
        v-if="tile.moreLabel"
        class="cms-colorlist__more"
        :to="route()"
        :target="linkTarget"
        rel="noopener"
        @click="track"
        >{{ tile.moreLabel }}</router-link
      >
      <span
        v-if="opensOrderslip && tile.state === constants.MARKET_OPEN"
        class="cms-colorlist__hint"
        ><span class="cms-colorlist__hint-full">Click price to trade</span
        ><span class="cms-colorlist__hint-tight">Click to trade</span></span
      >
      <router-link
        v-else
        class="cms-colorlist__hint"
        :to="route()"
        :target="linkTarget"
        rel="noopener"
        @click="track"
        >Open Market</router-link
      >
    </footer>
  </article>
</template>
<script src="./market-contract-list-colorlist.js"></script>
<style src="./market-contract-list-colorlist.scss" lang="scss"></style>
